import React, { useState, useEffect, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Upload, Loader2, Plus, Trash2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { useAppStore } from '../../store';
import { Production } from '../../types';
import { supabase } from '../../lib/supabase';
import EntityModal from './EntityModal';
import ErrorModal from './ErrorModal';
import ConfirmModal from './ConfirmModal';
import { formatCurrency } from '../../utils/formatters';

interface ProductionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (production: Omit<Production, 'id' | 'created_at' | 'updated_at'>) => void;
  initialData?: Production;
}

// Define localStorage keys
const STORAGE_KEYS = {
  APPROVED_FILE: 'production_approved_file',
  DTF_FILE: 'production_dtf_file',
  EMBROIDERY_FILE: 'production_embroidery_file',
  PAYMENT_RECEIPT: 'production_payment_receipt',
  FORM_DATA: 'production_form_data',
  FILE_NAMES: 'production_file_names'
};

const ProductionModal: React.FC<ProductionModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
}) => {
  const { clients, addClient } = useAppStore();
  const [formData, setFormData] = useState({
    name: '',
    client_id: '',
    approved_file: '',
    dtf_file: '',
    embroidery_file: '',
    delivery_date: '',
    salesperson: 'Mateo' as const,
    payment_receipt: '',
    status: 'pending' as const,
    production_value: '0',
    comment: '',
  });

  const [isUploading, setIsUploading] = useState({
    approved: false,
    dtf: false,
    embroidery: false,
    receipt: false,
  });

  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [fileToDelete, setFileToDelete] = useState<{field: string, fileName: string} | null>(null);
  const [isDeleteFileModalOpen, setIsDeleteFileModalOpen] = useState(false);
  const [fileNames, setFileNames] = useState<Record<string, string[]>>({});
  const [formModified, setFormModified] = useState(false);
  const [isConfirmCloseModalOpen, setIsConfirmCloseModalOpen] = useState(false);

  // Load data from localStorage when modal opens
  useEffect(() => {
    if (isOpen) {
      try {
            /* ─── NUEVO ─── */
    if (!initialData) {
      // Es “Nueva Producción” → limpiar todo
      localStorage.removeItem(STORAGE_KEYS.APPROVED_FILE);
      localStorage.removeItem(STORAGE_KEYS.DTF_FILE);
      localStorage.removeItem(STORAGE_KEYS.EMBROIDERY_FILE);
      localStorage.removeItem(STORAGE_KEYS.PAYMENT_RECEIPT);
      localStorage.removeItem(STORAGE_KEYS.FORM_DATA);
      localStorage.removeItem(STORAGE_KEYS.FILE_NAMES);

      // Reiniciar campos del formulario
      setFormData({
        name: '',
        client_id: '',
        approved_file: '',
        dtf_file: '',
        embroidery_file: '',
        delivery_date: '',
        salesperson: 'Mateo',
        payment_receipt: '',
        status: 'pending',
        production_value: '0',
        comment: '',
      });

      setFileNames({});
      setFormModified(false);
      return;          // ← saltamos el resto del try{} (no cargamos nada viejo)
    }
    /* ───────────── */

        // Load files
        const approvedFile = localStorage.getItem(STORAGE_KEYS.APPROVED_FILE);
        const dtfFile = localStorage.getItem(STORAGE_KEYS.DTF_FILE);
        const embroideryFile = localStorage.getItem(STORAGE_KEYS.EMBROIDERY_FILE);
        const paymentReceipt = localStorage.getItem(STORAGE_KEYS.PAYMENT_RECEIPT);
        
        // Load form data
        const savedFormData = localStorage.getItem(STORAGE_KEYS.FORM_DATA);
        const parsedFormData = savedFormData ? JSON.parse(savedFormData) : null;
        
        // Load file names
        const savedFileNames = localStorage.getItem(STORAGE_KEYS.FILE_NAMES);
        const parsedFileNames = savedFileNames ? JSON.parse(savedFileNames) : {};
        
        if (initialData) {
          // If editing an existing production
          setFormData({
            name: initialData.name,
            client_id: initialData.client_id,
            approved_file: initialData.approved_file || '',
            dtf_file: initialData.dtf_file || '',
            embroidery_file: initialData.embroidery_file || '',
            delivery_date: initialData.delivery_date,
            salesperson: initialData.salesperson,
            payment_receipt: initialData.payment_receipt || '',
            status: initialData.status,
            production_value: initialData.production_value?.toString() || '0',
            comment: initialData.comment || '',
          });

          if (initialData.file_names) {
  setFileNames(JSON.parse(initialData.file_names));
}

          
          // Check if we have saved changes for this production
          if (parsedFormData) {
            // Apply saved changes on top of the database data
            setFormData(prev => ({
              ...prev,
              ...(parsedFormData || {}),
              approved_file: approvedFile || prev.approved_file,
              dtf_file: dtfFile || prev.dtf_file,
              embroidery_file: embroideryFile || prev.embroidery_file,
              payment_receipt: paymentReceipt || prev.payment_receipt,
            }));
            
            // Apply saved file names
            if (parsedFileNames) {
              setFileNames(parsedFileNames);
            }
            
            setFormModified(true);
          }
        } else {
          // If creating a new production
          // Update state with saved data
          setFormData(prev => ({
            ...prev,
            ...(parsedFormData || {}),
            approved_file: approvedFile || '',
            dtf_file: dtfFile || '',
            embroidery_file: embroideryFile || '',
            payment_receipt: paymentReceipt || '',
          }));
          
          // Apply saved file names
          if (parsedFileNames) {
            setFileNames(parsedFileNames);
          }
        }

        // Reset form modified flag
        setFormModified(false);
      } catch (error) {
        console.error('Error loading data from localStorage:', error);
        // If there's an error, just continue with empty form
      }
    }
  }, [isOpen, initialData]);

  // Save form data to localStorage when it changes
  useEffect(() => {
    if (isOpen && formModified) {
      try {
        // Save form data without the file URLs (we store those separately)
        const formDataToSave = {
          ...formData,
          approved_file: undefined,
          dtf_file: undefined,
          embroidery_file: undefined,
          payment_receipt: undefined,
        };
        
        localStorage.setItem(STORAGE_KEYS.FORM_DATA, JSON.stringify(formDataToSave));
        
        // Save file URLs separately
        if (formData.approved_file) {
          localStorage.setItem(STORAGE_KEYS.APPROVED_FILE, formData.approved_file);
        }
        
        if (formData.dtf_file) {
          localStorage.setItem(STORAGE_KEYS.DTF_FILE, formData.dtf_file);
        }
        
        if (formData.embroidery_file) {
          localStorage.setItem(STORAGE_KEYS.EMBROIDERY_FILE, formData.embroidery_file);
        }
        
        if (formData.payment_receipt) {
          localStorage.setItem(STORAGE_KEYS.PAYMENT_RECEIPT, formData.payment_receipt);
        }
        
        // Save file names
        localStorage.setItem(STORAGE_KEYS.FILE_NAMES, JSON.stringify(fileNames));
      } catch (error) {
        console.error('Error saving data to localStorage:', error);
        // If there's an error saving, just continue
      }
    }
  }, [formData, isOpen, formModified, fileNames]);

  const handleFileUpload = async (files: FileList, type: 'approved' | 'dtf' | 'embroidery' | 'receipt') => {
    const uploadStateMap = {
      approved: 'approved_file',
      dtf: 'dtf_file',
      embroidery: 'embroidery_file',
      receipt: 'payment_receipt',
    };

    const storageKeyMap = {
      approved: STORAGE_KEYS.APPROVED_FILE,
      dtf: STORAGE_KEYS.DTF_FILE,
      embroidery: STORAGE_KEYS.EMBROIDERY_FILE,
      receipt: STORAGE_KEYS.PAYMENT_RECEIPT,
    };

    try {
      setIsUploading(prev => ({ ...prev, [type]: true }));

      const uploadedUrls: string[] = [];
      const uploadedFileNames: string[] = [];

      // Process each file in the FileList
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Create a unique file name
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `${type}/${fileName}`;

        // Upload file to Supabase Storage
        const { error: uploadError } = await supabase.storage
          .from('production-files')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('production-files')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
        uploadedFileNames.push(file.name);
      }

      // Save the original file names
      setFileNames(prev => ({
        ...prev,
        [uploadStateMap[type]]: uploadedFileNames
      }));

      // Join multiple URLs with a delimiter if there are multiple files
      const urlString = uploadedUrls.join('|');

      setFormData(prev => ({
        ...prev,
        [uploadStateMap[type]]: urlString,
      }));
      setFormModified(true);

      // Save to localStorage
      localStorage.setItem(storageKeyMap[type], urlString);
      
      // Save file names to localStorage
      localStorage.setItem(STORAGE_KEYS.FILE_NAMES, JSON.stringify({
        ...fileNames,
        [uploadStateMap[type]]: uploadedFileNames
      }));
    } catch (error: any) {
      console.error('Error uploading file:', error);
      setErrorMessage(error.message || 'Error al subir el archivo. Por favor, intente nuevamente.');
      setErrorModalOpen(true);
    } finally {
      setIsUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleDeleteFile = (field: string) => {
    // Extract file names from URL string
    const urls = (formData[field as keyof typeof formData] as string).split('|');
    const fileNamesArray = fileNames[field] || urls.map(url => url.split('/').pop() || 'archivo');
    
    setFileToDelete({ field, fileName: fileNamesArray.join(', ') });
    setIsDeleteFileModalOpen(true);
  };

  const confirmDeleteFile = () => {
    if (!fileToDelete) return;
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      [fileToDelete.field]: '',
    }));
    
    // If we're creating a new production, also remove from localStorage
    if (!initialData) {
      const storageKeyMap: Record<string, string> = {
        approved_file: STORAGE_KEYS.APPROVED_FILE,
        dtf_file: STORAGE_KEYS.DTF_FILE,
        embroidery_file: STORAGE_KEYS.EMBROIDERY_FILE,
        payment_receipt: STORAGE_KEYS.PAYMENT_RECEIPT,
      };
      
      const storageKey = storageKeyMap[fileToDelete.field];
      if (storageKey) {
        localStorage.removeItem(storageKey);
      }
    }
    
    // Remove from fileNames
    setFileNames(prev => {
      const newFileNames = {...prev};
      delete newFileNames[fileToDelete.field];
      return newFileNames;
    });
    
    // Update fileNames in localStorage
    localStorage.setItem(STORAGE_KEYS.FILE_NAMES, JSON.stringify({
      ...fileNames,
      [fileToDelete.field]: undefined
    }));
    
    setFileToDelete(null);
    setFormModified(true);
  };

  const getFileNameFromUrl = (url: string, field: string): string => {
    // First check if we have the original filenames stored
    if (fileNames[field] && fileNames[field].length > 0) {
      return fileNames[field].join(', ');
    }
    
    // Otherwise extract from URL
    const urls = url.split('|');
    return urls.map(u => {
      const parts = u.split('/');
      return parts[parts.length - 1] || 'archivo';
    }).join(', ');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.approved_file) {
      setErrorMessage('La ficha aprobada es obligatoria');
      setErrorModalOpen(true);
      return;
    }
    
    onSave({
      ...formData,
  production_value: parseFloat(formData.production_value),
  file_names: JSON.stringify(fileNames),
    });
    onClose();
  };

  const handleNewClient = async (client: any) => {
    try {
      const newClient = await addClient(client);
      setFormData(prev => ({ ...prev, client_id: newClient.id }));
      setIsClientModalOpen(false);
      setFormModified(true);
    } catch (error: any) {
      console.error('Error creating client:', error);
      setErrorMessage('Error al crear el cliente');
      setErrorModalOpen(true);
    }
  };

  const handleCloseClick = () => {
    if (formModified) {
      setIsConfirmCloseModalOpen(true);
    } else {
      onClose();
    }
  };

  // Clear localStorage and close modal
  const handleConfirmClose = () => {
    // Clear localStorage when modal is closed
    localStorage.removeItem(STORAGE_KEYS.APPROVED_FILE);
    localStorage.removeItem(STORAGE_KEYS.DTF_FILE);
    localStorage.removeItem(STORAGE_KEYS.EMBROIDERY_FILE);
    localStorage.removeItem(STORAGE_KEYS.PAYMENT_RECEIPT);
    localStorage.removeItem(STORAGE_KEYS.FORM_DATA);
    localStorage.removeItem(STORAGE_KEYS.FILE_NAMES);
    
    // Reset form modified flag
    setFormModified(false);
    
    // Close confirm modal
    setIsConfirmCloseModalOpen(false);
    
    // Call the original onClose function
    onClose();
  };

  // Format production value as currency
  const formatProductionValueAsCurrency = (value: string) => {
    if (!value || isNaN(parseFloat(value))) return '';
    return formatCurrency(parseFloat(value));
  };

  // Render file links for multiple files
  const renderFileLinks = (urls: string, field: string) => {
    if (!urls) return null;
    
    const urlArray = urls.split('|');
    const fileNamesArray = fileNames[field] || urlArray.map(url => url.split('/').pop() || 'archivo');
    
    return (
      <div className="flex flex-col gap-1">
        {urlArray.map((url, index) => (
          <a
            key={index}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
          >
            {fileNamesArray[index] || `Archivo ${index + 1}`}
          </a>
        ))}
      </div>
    );
  };

  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={handleCloseClick}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50" />
          <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[600px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white dark:bg-gray-800 p-6 shadow-lg overflow-y-auto">
            <Dialog.Title className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
              {initialData ? 'Editar' : 'Nueva'} Producción
            </Dialog.Title>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <Input
                  label="Nombre"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, name: e.target.value }));
                    setFormModified(true);
                  }}
                  required
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Cliente
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Select
                        label=""
                        value={formData.client_id}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, client_id: value }));
                          setFormModified(true);
                        }}
                        options={clients.map(client => ({
                          value: client.id,
                          label: client.name,
                        }))}
                        required
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsClientModalOpen(true)}
                      icon={<Plus className="h-4 w-4" />}
                      className="h-12 flex items-center justify-center"
                    >
                      Nuevo
                    </Button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Ficha Aprobada
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const files = e.target.files;
                        if (files && files.length > 0) handleFileUpload(files, 'approved');
                      }}
                      className="hidden"
                      id="approved-file"
                      multiple
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('approved-file')?.click()}
                      disabled={isUploading.approved}
                      icon={isUploading.approved ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                      className="h-12 flex items-center justify-center"
                    >
                      Subir Ficha
                    </Button>
                    {formData.approved_file && (
                      <div className="flex items-center gap-2">
                        {renderFileLinks(formData.approved_file, 'approved_file')}
                        <button
                          type="button"
                          onClick={() => handleDeleteFile('approved_file')}
                          className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  {!formData.approved_file && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      La ficha aprobada es obligatoria
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Archivo DTF
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const files = e.target.files;
                        if (files && files.length > 0) handleFileUpload(files, 'dtf');
                      }}
                      className="hidden"
                      id="dtf-file"
                      multiple
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('dtf-file')?.click()}
                      disabled={isUploading.dtf}
                      icon={isUploading.dtf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                      className="h-12 flex items-center justify-center"
                    >
                      Subir DTF
                    </Button>
                    {formData.dtf_file && (
                      <div className="flex items-center gap-2">
                        {renderFileLinks(formData.dtf_file, 'dtf_file')}
                        <button
                          type="button"
                          onClick={() => handleDeleteFile('dtf_file')}
                          className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Archivo Bordado
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const files = e.target.files;
                        if (files && files.length > 0) handleFileUpload(files, 'embroidery');
                      }}
                      className="hidden"
                      id="embroidery-file"
                      multiple
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('embroidery-file')?.click()}
                      disabled={isUploading.embroidery}
                      icon={isUploading.embroidery ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                      className="h-12 flex items-center justify-center"
                    >
                      Subir Bordado
                    </Button>
                    {formData.embroidery_file && (
                      <div className="flex items-center gap-2">
                        {renderFileLinks(formData.embroidery_file, 'embroidery_file')}
                        <button
                          type="button"
                          onClick={() => handleDeleteFile('embroidery_file')}
                          className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <Input
                  type="date"
                  label="Fecha de Entrega"
                  id="delivery_date"
                  name="delivery_date"
                  value={formData.delivery_date}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, delivery_date: e.target.value }));
                    setFormModified(true);
                  }}
                  required
                />

                <Select
                  label="Vendedor"
                  value={formData.salesperson}
                  onChange={(value) => {
                    setFormData(prev => ({ ...prev, salesperson: value as 'Mateo' | 'Male' }));
                    setFormModified(true);
                  }}
                  options={[
                    { value: 'Mateo', label: 'Mateo' },
                    { value: 'Male', label: 'Male' },
                  ]}
                  required
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Comprobante de Pago
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const files = e.target.files;
                        if (files && files.length > 0) handleFileUpload(files, 'receipt');
                      }}
                      className="hidden"
                      id="payment-receipt"
                      multiple
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('payment-receipt')?.click()}
                      disabled={isUploading.receipt}
                      icon={isUploading.receipt ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                      className="h-12 flex items-center justify-center"
                    >
                      Subir Comprobante
                    </Button>
                    {formData.payment_receipt && (
                      <div className="flex items-center gap-2">
                        {renderFileLinks(formData.payment_receipt, 'payment_receipt')}
                        <button
                          type="button"
                          onClick={() => handleDeleteFile('payment_receipt')}
                          className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Valor Producción
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      id="production_value"
                      name="production_value"
                      value={formData.production_value}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, production_value: e.target.value }));
                        setFormModified(true);
                      }}
                      min="0"
                      step="0.01"
                      required
                      className="w-full h-12 pl-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-500 dark:text-gray-400">
                      $
                    </div>
                  </div>
                  {formData.production_value && !isNaN(parseFloat(formData.production_value)) && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {formatProductionValueAsCurrency(formData.production_value)}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Comentarios
                  </label>
                  <textarea
                    id="comment"
                    name="comment"
                    rows={3}
                    value={formData.comment}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, comment: e.target.value }));
                      setFormModified(true);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                    placeholder="Agregar comentarios adicionales..."
                  />
                </div>

                {initialData && (
                  <Select
                    label="Estado"
                    value={formData.status}
                    onChange={(value) => {
                      setFormData(prev => ({ ...prev, status: value as Production['status'] }));
                      setFormModified(true);
                    }}
                    options={[
                      { value: 'pending', label: 'Pendiente' },
                      { value: 'in_progress', label: 'En Proceso' },
                      { value: 'finished', label: 'Terminado' },
                    ]}
                    required
                  />
                )}
              </div>
              
              <div className="mt-6 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseClick}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={!formData.name || !formData.client_id || !formData.delivery_date || !formData.salesperson || !formData.approved_file}
                >
                  {initialData ? 'Guardar Cambios' : 'Crear Producción'}
                </Button>
              </div>
            </form>
            
            <Dialog.Close asChild>
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                aria-label="Close"
                onClick={(e) => {
                  e.preventDefault();
                  handleCloseClick();
                }}
              >
                <X className="h-6 w-6" />
              </button>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <EntityModal
        isOpen={isClientModalOpen}
        onClose={() => setIsClientModalOpen(false)}
        entityType="client"
        onSave={handleNewClient}
      />

      <ErrorModal
        isOpen={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        message={errorMessage}
      />

      <ConfirmModal
        isOpen={isDeleteFileModalOpen}
        onClose={() => {
          setIsDeleteFileModalOpen(false);
          setFileToDelete(null);
        }}
        onConfirm={confirmDeleteFile}
        title="Eliminar archivo"
        message={`¿Estás seguro de que querés eliminar el archivo ${fileToDelete?.fileName}?`}
        confirmText="Eliminar"
        variant="danger"
      />

      <ConfirmModal
        isOpen={isConfirmCloseModalOpen}
        onClose={() => setIsConfirmCloseModalOpen(false)}
        onConfirm={handleConfirmClose}
        title="¿Seguro que querés cancelar?"
        message="Se borrarán los datos cargados hasta el momento."
        confirmText="Aceptar"
        variant="warning"
      />
    </>
  );
};

export default ProductionModal;